import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { NavigationService } from 'src/app/core/navigation.service';
import { USER } from 'src/app/core/user.service';
import {
  DutyLogHttpClient,
  DutyOffParams,
} from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { FlLoadingService } from 'src/lib/fl-loading/fl-loading.service';
import { filterNull } from 'src/util/rxjs/operators';
import { UserViewModel } from 'src/view-models/user-view-model';
import { DutyOffFormComponent, DutyOffFormParams } from '../duty-off-form/duty-off-form.component';

@Component({
  selector: 'fl-duty-off-page',
  templateUrl: './duty-off-page.component.html',
  styleUrls: ['./duty-off-page.component.scss'],
})
export class DutyOffPageComponent implements OnInit, OnDestroy {
  public loadingKey = 'duty-off-form';
  get pilotName(): string {
    return this.user.fullName;
  }
  public onDestroy$ = new Subject<void>();
  public params$: Observable<DutyOffFormParams>;
  @ViewChild(DutyOffFormComponent)
  public formCmp: DutyOffFormComponent;
  private _dutiedOff = false;

  constructor(
    @Inject(USER) private user: UserViewModel,
    private dutyLogs: DutyLogHttpClient,
    private _nav: NavigationService,
    private _router: Router,
    private _loading: FlLoadingService,
    private _snackBar: MatSnackBar,
  ) {}

  public ngOnInit() {
    // If user is on duty = navigate to duty on page
    this.user.isOnDuty$
      .pipe(
        filter((isOnDuty) => !isOnDuty),
        switchMap(() => this.user.latestShift$),
        filterNull(),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        if (!this._dutiedOff) {
          this._router.navigate(['duty-logs', 'duty-on']);
        }
      });

    const currentShift$ = this.user.latestShift$.pipe(filterNull());
    this.params$ = this.dutyLogs.getMany({ pilotEmployeeId: this.user.id }).pipe(
      switchMap(() => combineLatest([currentShift$, this.user.previousShift$])),
      map(([currentShift, previousShift]) => ({
        currentShift,
        prevEntryEndTime: previousShift != null ? previousShift.dutyPeriodEndTime : undefined,
      })),
    );
  }

  public async dutyOff() {
    this._loading.register(this.loadingKey);
    this._dutiedOff = true;
    try {
      const dutyOff: DutyOffParams = this.formCmp.form.value;
      await this.dutyLogs.dutyOff(dutyOff).toPromise();
      this._snackBar.open('Successfully ended shift.', undefined, {
        duration: 2000,
      });
      // avoid crazy redirects - navigate back to duty logs index if
      // we were previously on a duty log page
      if (this._nav.matchesPreviousUrl('duty', 'unit')) {
        this._router.navigate(['duty-logs']);
      } else {
        this._nav.goBack();
      }
    } catch (err) {
      this._dutiedOff = false;
      this._snackBar.open('Error ending shift', undefined, { duration: 2000 });
      throw err;
    } finally {
      this._loading.resolveAll(this.loadingKey);
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }
}
